import Typesense, { Client } from 'typesense'
import { SearchResponseHit } from 'typesense/lib/Typesense/Documents'
import {
  DiscoverieSearchResponseRecord,
  QuickSearchParams,
  QuickSearchResponse,
  DiscoverieSearchResponse,
} from '@/search/types'
import { QuickSearchSortOption } from '@/QuickSearch/useQuickSearch'
import { parseDiscoverieScopedQuery } from '@/lib/discoverie/querySyntax'

/**
 * Must match `products_preset.filter_by` in Typesense. Request-level `filter_by`
 * replaces the preset’s value entirely, so we re-apply this whenever we add filters.
 */
const PRODUCTS_PRESET_BASE_FILTER = 'type:=[product] && live:=true'

/** Fields needed for product-page recommendations (RecommendedProducts card). */
const RECOMMENDATION_INCLUDE_FIELDS =
  'title,creators,thumbnail_url,product_url,object_id,price_usd,discount_usd,price_gbp,discount_gbp'

/**
 * Field order must match `query_by` on the `products_preset` in Typesense (and blog searches below).
 * Same weights as `TypesenseManager.cs` for the legacy products index.
 */
const PRODUCTS_QUERY_BY_WEIGHTS = '10,5,5,1'

/** Discoverie / ebooks: title and author should outweigh body text. */
const EBOOKS_QUERY_BY_WEIGHTS = '5,4,2'

/** Related products: prefer creator affinity, then title. */
const RECOMMENDATIONS_QUERY_BY_WEIGHTS = '6,4'

/** Skip $0 / £0 list-price items in “related” (downloads, promos, etc.). */
const RECOMMENDATIONS_MIN_PRICE_FILTER = '(price_usd:>0 || price_gbp:>0)'

const TYPESENSE_CONNECTION_TIMEOUT_SECONDS = 10
const TYPESENSE_AUTOCOMPLETE_TIMEOUT_SECONDS = 3
const TYPESENSE_NUM_RETRIES = 1
const TYPESENSE_CIRCUIT_BREAKER_TIMEOUT_THRESHOLD = 3
const TYPESENSE_CIRCUIT_BREAKER_COOLDOWN_MS = 30_000
const TYPESENSE_CIRCUIT_BREAKER_WARNING_INTERVAL_MS = 10_000

let consecutiveTypesenseTimeouts = 0
let typesenseCircuitOpenUntil = 0
let nextTypesenseCircuitWarningAfter = 0

function getErrorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code
    return typeof code === 'string' ? code : undefined
  }

  return undefined
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function isTypesenseTimeoutError(error: unknown): boolean {
  const code = getErrorCode(error)
  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true

  const message = getErrorMessage(error).toLowerCase()
  return message.includes('timeout') || message.includes('timed out')
}

function getSearchTimeoutSeconds(params: QuickSearchParams) {
  return params.omitSortBy
    ? TYPESENSE_AUTOCOMPLETE_TIMEOUT_SECONDS
    : TYPESENSE_CONNECTION_TIMEOUT_SECONDS
}

function getSearchContext(params: QuickSearchParams, source: string) {
  const trimmedQuery = params.query.trim()

  return {
    source,
    collection: params.collection,
    queryLength: trimmedQuery.length,
    queryType:
      trimmedQuery.length === 0
        ? 'empty'
        : trimmedQuery === '*'
          ? 'wildcard'
          : 'text',
    page: params.page || 1,
    limit: params.limit || 10,
    filterCount: (params.filterBy?.length ?? 0) + (params.customFilter ? 1 : 0),
    includeFieldCount: params.includeFields.length,
    hasSort: Boolean(params.sortBy),
    omitSortBy: Boolean(params.omitSortBy),
    timeoutSeconds: getSearchTimeoutSeconds(params),
    numRetries: TYPESENSE_NUM_RETRIES,
  }
}

function warnTypesenseTimeout(
  message: string,
  error: unknown,
  context: Record<string, unknown>
) {
  console.warn(message, {
    ...context,
    errorCode: getErrorCode(error),
    errorMessage: getErrorMessage(error),
  })
}

function isTypesenseCircuitOpen() {
  return Date.now() < typesenseCircuitOpenUntil
}

function recordTypesenseSuccess() {
  consecutiveTypesenseTimeouts = 0
  typesenseCircuitOpenUntil = 0
}

function recordTypesenseTimeout() {
  consecutiveTypesenseTimeouts += 1
  if (
    consecutiveTypesenseTimeouts >= TYPESENSE_CIRCUIT_BREAKER_TIMEOUT_THRESHOLD
  ) {
    typesenseCircuitOpenUntil =
      Date.now() + TYPESENSE_CIRCUIT_BREAKER_COOLDOWN_MS
  }
}

function warnTypesenseCircuitOpen(
  message: string,
  context: Record<string, unknown>
) {
  const now = Date.now()
  if (now < nextTypesenseCircuitWarningAfter) return

  nextTypesenseCircuitWarningAfter =
    now + TYPESENSE_CIRCUIT_BREAKER_WARNING_INTERVAL_MS
  console.warn(message, {
    ...context,
    timeoutThreshold: TYPESENSE_CIRCUIT_BREAKER_TIMEOUT_THRESHOLD,
    cooldownMs: TYPESENSE_CIRCUIT_BREAKER_COOLDOWN_MS,
    openForMs: Math.max(typesenseCircuitOpenUntil - now, 0),
  })
}

function parseDiscoverieQuery(query: string): {
  query: string
  queryBy: 'title' | 'title,author,text'
  queryByWeights?: string
  numTypos?: number
  titleFilter?: string
  authorFilter?: string
} {
  const parsedQuery = parseDiscoverieScopedQuery(query)

  if (
    parsedQuery.titleFilter &&
    !parsedQuery.authorFilter &&
    !parsedQuery.baseQuery
  ) {
    return {
      query: parsedQuery.titleFilter,
      queryBy: 'title',
      numTypos: 0,
    }
  }

  if (parsedQuery.titleFilter || parsedQuery.authorFilter) {
    return {
      query: parsedQuery.baseQuery || '*',
      queryBy: 'title,author,text',
      queryByWeights: EBOOKS_QUERY_BY_WEIGHTS,
      titleFilter: parsedQuery.titleFilter,
      authorFilter: parsedQuery.authorFilter,
    }
  }

  return {
    query,
    queryBy: 'title,author,text',
    queryByWeights: EBOOKS_QUERY_BY_WEIGHTS,
  }
}

/**
 * Request-level params override preset defaults. Tighten typos for short header queries;
 * use full typo tolerance on the search page.
 */
function applyProductsRelevanceTuning(
  searchParams: Record<string, unknown>,
  mode: 'autocomplete' | 'full'
): void {
  searchParams.query_by_weights = PRODUCTS_QUERY_BY_WEIGHTS
  searchParams.demote_synonym_match = true

  if (mode === 'autocomplete') {
    searchParams.num_typos = 1
    searchParams.prioritize_token_position = true
  } else {
    searchParams.text_match_type = 'max_score'
  }
}

function escapeTypesenseFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/`/g, '\\`')
}

function buildExactStringListFilter(
  field: string,
  values: string[] | undefined
): string | null {
  const normalizedValues = Array.from(
    new Set(
      values
        ?.map(value => value.trim())
        .filter(value => value.length > 0) || []
    )
  )

  if (normalizedValues.length === 0) return null

  return `${field}:=[${normalizedValues
    .map(value => `\`${escapeTypesenseFilterValue(value)}\``)
    .join(',')}]`
}

function mapFacetCounts(response: any): QuickSearchResponse['facets'] {
  return (
    response.facet_counts?.map((facet: any) => ({
      field: facet.field_name,
      counts:
        facet.counts?.map((count: any) => ({
          count: count.count,
          highlighted: count.highlighted,
          value: String(count.value),
        })) || [],
    })) || []
  )
}

/** SDK `SearchParams` omits several server-side tuning fields; use a loose payload. */
function buildProductSearchParams(
  params: QuickSearchParams,
  isBlogSearch: boolean,
  options: { excludeProductTypeFilter?: boolean } = {}
): any {
  const searchParams: Record<string, unknown> = {
    q: params.query || '*',
    include_fields: params.includeFields.join(','),
    limit: params.limit || 10,
    page: params.page || 1,
  }

  if (!params.omitSortBy) {
    searchParams.sort_by = params.sortBy || TypesenseClient.SortBy.RELEVANCE
  }

  if (isBlogSearch) {
    searchParams.query_by = 'keywords,title,creators,text'
  } else {
    searchParams.preset = 'products_preset'
    searchParams.facet_by = 'product_type'
    searchParams.max_facet_values = 20
  }

  const mode = params.omitSortBy ? 'autocomplete' : 'full'
  applyProductsRelevanceTuning(searchParams, mode)

  const filterParts: string[] = []

  if (params.filterBy && params.filterBy.length > 0) {
    const filterStrings = params.filterBy.map(
      filter =>
        TypesenseClient.FilterBy[
          filter as keyof typeof TypesenseClient.FilterBy
        ]
    )
    filterParts.push(...filterStrings)
  }

  const productTypeFilter = buildExactStringListFilter(
    'product_type',
    params.productTypes
  )
  if (!options.excludeProductTypeFilter && productTypeFilter) {
    filterParts.push(productTypeFilter)
  }

  if (params.customFilter) {
    filterParts.push(params.customFilter)
  }

  if (filterParts.length > 0) {
    searchParams.filter_by = isBlogSearch
      ? filterParts.join(' && ')
      : [PRODUCTS_PRESET_BASE_FILTER, ...filterParts].join(' && ')
  }

  return searchParams
}

class TypesenseClient {
  private client: Client | null = null
  private autocompleteClient: Client | null = null

  public static SortBy = {
    RELEVANCE: '_text_match(buckets: 15):desc,sales_rank:asc',
    PRICE_LOW_TO_HIGH: 'price_usd:asc',
    PRICE_HIGH_TO_LOW: 'price_usd:desc',
    PRICE_ASC: 'price_usd:asc',
    PRICE_DESC: 'price_usd:desc',
    DATE_ADDED_NEW_TO_OLD: 'date_added:desc',
    DATE_ADDED_OLD_TO_NEW: 'date_added:asc',
    DATE_ADDED_ASC: 'date_added:asc', // Ascending = oldest first
    DATE_ADDED_DESC: 'date_added:desc', // Descending = newest first
    DISCOVERIE_BEST_MATCH: '_text_match:desc,page_number:asc',
    DISCOVERIE_TITLE: 'title:asc,_text_match:desc,page_number:asc',
    DISCOVERIE_AUTHOR: 'author:asc,title:asc,_text_match:desc',
    DISCOVERIE_NEWEST: 'product_id:desc,_text_match:desc,page_number:asc',
  }

  public static FilterBy = {
    IN_STOCK: 'in_stock:true',
    ON_SALE: 'discount_usd:>0',
  }

  constructor() {
    const host = process.env.NEXT_PUBLIC_TYPESENSE_HOST
    const apiKey = process.env.NEXT_PUBLIC_TYPESENSE_API_KEY
    const protocol =
      process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL === 'http' ? 'http' : 'https'
    const configuredPort = Number.parseInt(
      process.env.NEXT_PUBLIC_TYPESENSE_PORT || '',
      10
    )
    const port =
      Number.isInteger(configuredPort) && configuredPort > 0
        ? configuredPort
        : 443

    if (host && apiKey) {
      this.client = this.createClient(
        host,
        apiKey,
        port,
        protocol,
        TYPESENSE_CONNECTION_TIMEOUT_SECONDS
      )
      this.autocompleteClient = this.createClient(
        host,
        apiKey,
        port,
        protocol,
        TYPESENSE_AUTOCOMPLETE_TIMEOUT_SECONDS
      )
    }
  }

  private createClient(
    host: string,
    apiKey: string,
    port: number,
    protocol: 'http' | 'https',
    connectionTimeoutSeconds: number
  ) {
    return new Typesense.Client({
      nodes: [
        {
          host,
          port,
          protocol,
        },
      ],
      apiKey,
      connectionTimeoutSeconds,
      numRetries: TYPESENSE_NUM_RETRIES,
    })
  }

  private getProductSearchClient(params: QuickSearchParams) {
    return params.omitSortBy ? this.autocompleteClient : this.client
  }

  async searchEbooks(
    params: QuickSearchParams
  ): Promise<DiscoverieSearchResponse> {
    if (!this.client) {
      return Promise.resolve({ groupedHits: [], totalCount: 0 })
    }

    interface ebookSearchResponse {
      grouped_hits: {
        group_key: string[]
        hits: {
          document: {
            page_number: number
            title: string
            author?: string
            image_filename: string
            date_added?: number | string
            text?: string
            /** Present when requested via include_fields; used for productForDiscoverie(id). */
            object_id?: string
            product_id?: string | number
            download_file_id?: string | number
            download_file_name?: string
          }
          highlight: {
            text: {
              snippet: string
            }
          }
        }[]
      }[]
      found: number
    }

    // Build filter_by string
    let filterBy = 'live:true'
    if (params.purchasedProductIds) {
      filterBy += ` && product_id:[${params.purchasedProductIds}]`
    }
    if (params.discoverieProductId) {
      filterBy += ` && product_id:=${params.discoverieProductId}`
    }

    const discoverieQuery = parseDiscoverieQuery(params.query)
    if (discoverieQuery.titleFilter) {
      const escapedTitleFilter = discoverieQuery.titleFilter
        .replace(/\\/g, '\\\\')
        .replace(/`/g, '\\`')
      filterBy += ` && title:\`${escapedTitleFilter}\``
    }
    if (discoverieQuery.authorFilter) {
      const escapedAuthorFilter = discoverieQuery.authorFilter
        .replace(/\\/g, '\\\\')
        .replace(/`/g, '\\`')
      filterBy += ` && author:\`${escapedAuthorFilter}\``
    }

    const response = (await this.client
      .collections('ebooks')
      .documents()
      .search({
        q: discoverieQuery.query,
        query_by: discoverieQuery.queryBy,
        ...(discoverieQuery.queryByWeights
          ? { query_by_weights: discoverieQuery.queryByWeights }
          : {}),
        ...(discoverieQuery.numTypos != null
          ? { num_typos: discoverieQuery.numTypos }
          : {}),
        text_match_type: 'max_score',
        demote_synonym_match: true,
        page: params.page || 1,
        filter_by: filterBy,
        sort_by: params.sortBy || TypesenseClient.SortBy.DISCOVERIE_BEST_MATCH,
        group_by: 'product_id',
        group_limit: 15,
        include_fields: params.includeFields.join(','),
        limit: params.limit || 10,
        highlight_full_fields: 'text',
        highlight_start_tag: '<mark>',
        highlight_end_tag: '</mark>',
        snippet_threshold: 150,
      } as any)) as ebookSearchResponse

    const groupedHits =
      response.grouped_hits?.map(groupedHit => {
        const doc = groupedHit.hits[0].document
        // GraphQL productForDiscoverie(id) uses DB product id; align with Typesense product_id when present.
        const discoverieLookupId =
          doc.product_id != null && String(doc.product_id).trim() !== ''
            ? String(doc.product_id)
            : doc.object_id != null && String(doc.object_id).trim() !== ''
              ? String(doc.object_id)
              : String(groupedHit.group_key[0])
        const parsedDownloadFileId = Number(doc.download_file_id)

        return {
          title: doc.title,
          author: doc.author,
          objectId: discoverieLookupId,
          downloadFileId:
            Number.isInteger(parsedDownloadFileId) && parsedDownloadFileId > 0
              ? parsedDownloadFileId
              : undefined,
          downloadFileName: doc.download_file_name,
          dateAdded: doc.date_added,
          pages: groupedHit.hits.map(hit => ({
            image: hit.document.image_filename,
            pageNumber: hit.document.page_number,
            textMatch:
              hit.highlight?.text?.snippet ||
              hit.document.text?.substring(0, 150) + '...',
          })),
        }
      }) || []

    return {
      groupedHits,
      totalCount: response.found,
    } as DiscoverieSearchResponse
  }

  async getEbookPageCount(): Promise<number | null> {
    if (!this.client) {
      return null
    }

    try {
      const response = (await this.client
        .collections('ebooks')
        .documents()
        .search({
          q: '*',
          query_by: 'title',
          filter_by: 'live:true',
          include_fields: 'id',
          limit: 1,
          page: 1,
        } as any)) as { found?: number }

      return typeof response.found === 'number' ? response.found : null
    } catch (error) {
      console.error('Typesense ebook page count error:', error)
      return null
    }
  }

  async getLatestEbooks(limit = 9): Promise<DiscoverieSearchResponseRecord[]> {
    if (!this.client) {
      return []
    }

    interface LatestEbookSearchResponse {
      grouped_hits?: {
        group_key: string[]
        hits: {
          document: {
            page_number: number
            title: string
            author?: string
            image_filename: string
            date_added?: number | string
            object_id?: string
            product_id?: string | number
          }
        }[]
      }[]
    }

    try {
      const response = (await this.client
        .collections('ebooks')
        .documents()
        .search({
          q: '*',
          query_by: 'title',
          filter_by: 'live:true',
          sort_by: 'product_id:desc,page_number:asc',
          group_by: 'product_id',
          group_limit: 1,
          include_fields:
            'object_id,product_id,image_filename,title,author,page_number,date_added',
          limit,
          page: 1,
        } as any)) as LatestEbookSearchResponse

      return (
        response.grouped_hits?.map(groupedHit => {
          const doc = groupedHit.hits[0].document
          const objectId =
            doc.product_id != null && String(doc.product_id).trim() !== ''
              ? String(doc.product_id)
              : doc.object_id != null && String(doc.object_id).trim() !== ''
                ? String(doc.object_id)
                : String(groupedHit.group_key[0])

          return {
            objectId,
            title: doc.title,
            author: doc.author,
            dateAdded: doc.date_added,
            pages: [
              {
                image: doc.image_filename,
                pageNumber: doc.page_number,
                textMatch: '',
              },
            ],
          }
        }) ?? []
      )
    } catch (error) {
      console.error('Typesense latest ebooks error:', error)
      return []
    }
  }

  async searchProducts(
    params: QuickSearchParams
  ): Promise<QuickSearchResponse> {
    const client = this.getProductSearchClient(params)

    if (!client) {
      return Promise.resolve({ hits: [], totalCount: 0 })
    }
    const productSearchClient = client

    const isBlogSearch =
      !!params.customFilter && params.customFilter.includes('type:Post')
    const source = isBlogSearch
      ? 'blog-search'
      : params.omitSortBy
        ? 'autocomplete'
        : 'product-search'
    const searchContext = getSearchContext(params, source)

    if (isTypesenseCircuitOpen()) {
      warnTypesenseCircuitOpen(
        'Typesense circuit open, returning empty results',
        searchContext
      )
      return { hits: [], totalCount: 0, facets: [] }
    }

    try {
      const searchParams = buildProductSearchParams(params, isBlogSearch)

      const response = await productSearchClient
        .collections('products')
        .documents()
        .search(searchParams)
      let facets = mapFacetCounts(response)

      if (!isBlogSearch && params.productTypes && params.productTypes.length > 0) {
        try {
          const facetSearchParams = buildProductSearchParams(
            { ...params, limit: 1, page: 1 },
            isBlogSearch,
            { excludeProductTypeFilter: true }
          )
          const facetResponse = await productSearchClient
            .collections('products')
            .documents()
            .search(facetSearchParams)
          facets = mapFacetCounts(facetResponse)
        } catch (facetError) {
          console.warn('Typesense product type facet refresh failed:', facetError)
        }
      }

      recordTypesenseSuccess()

      // Determine result type from document
      const getResultType = (doc: any): 'product' | 'blog' | 'page' => {
        if (doc.type === 'Post') return 'blog'
        if (doc.product_type === 'page') return 'page'
        if (doc.product_type === 'article') return 'blog'
        return 'product'
      }

      return {
        hits:
          response.hits?.map((hit: SearchResponseHit<any>) => ({
            title: hit.document.title,
            creators: hit.document.creators,
            creatorNames: hit.document.creator_names,
            creatorSlugs: hit.document.creator_slugs,
            thumbnailUrl: hit.document.thumbnail_url,
            productUrl: hit.document.product_url,
            objectId: hit.document.object_id,
            priceUsd: hit.document.price_usd,
            discountUsd: hit.document.discount_usd,
            priceGbp: hit.document.price_gbp,
            discountGbp: hit.document.discount_gbp,
            productType: hit.document.product_type,
            productTypeDescription: hit.document.product_type_description,
            inStock: hit.document.in_stock,
            categories: hit.document.categories || [],
            resultType: getResultType(hit.document),
            options: hit.document.options || 0,
            dateAdded: hit.document.date_added,
          })) || [],
        totalCount: response.found || 0,
        facets,
      } as QuickSearchResponse
    } catch (error: any) {
      if (isTypesenseTimeoutError(error)) {
        warnTypesenseTimeout(
          'Typesense search timeout, returning empty results',
          error,
          searchContext
        )
        recordTypesenseTimeout()
        return { hits: [], totalCount: 0, facets: [] }
      }

      // If preset doesn't support facet_by or returns 404, try without it
      const is404Error =
        error?.httpStatus === 404 ||
        error?.status === 404 ||
        (error instanceof Error && error.message.includes('404')) ||
        (error?.message && String(error.message).includes('404'))

      if (is404Error) {
        try {
          const searchParamsRetry = buildProductSearchParams(
            params,
            isBlogSearch
          )

          const response = await productSearchClient
            .collections('products')
            .documents()
            .search(searchParamsRetry)

          recordTypesenseSuccess()

          const getResultType = (doc: any): 'product' | 'blog' | 'page' => {
            if (doc.type === 'Post') return 'blog'
            if (doc.product_type === 'page') return 'page'
            if (doc.product_type === 'article') return 'blog'
            return 'product'
          }

          return {
            hits:
              response.hits?.map((hit: SearchResponseHit<any>) => ({
                title: hit.document.title,
                creators: hit.document.creators,
                thumbnailUrl: hit.document.thumbnail_url,
                productUrl: hit.document.product_url,
                objectId: hit.document.object_id,
                priceUsd: hit.document.price_usd,
                discountUsd: hit.document.discount_usd,
                priceGbp: hit.document.price_gbp,
                discountGbp: hit.document.discount_gbp,
                productType: hit.document.product_type,
                productTypeDescription: hit.document.product_type_description,
                inStock: hit.document.in_stock,
                categories: hit.document.categories || [],
                resultType: getResultType(hit.document),
                options: hit.document.options || 0,
                dateAdded: hit.document.date_added,
              })) || [],
            totalCount: response.found || 0,
            facets: mapFacetCounts(response),
          } as QuickSearchResponse
        } catch (retryError) {
          if (isTypesenseTimeoutError(retryError)) {
            warnTypesenseTimeout(
              'Typesense search retry timeout, returning empty results',
              retryError,
              searchContext
            )
            recordTypesenseTimeout()
          }

          console.error('Typesense search error:', retryError)
          return { hits: [], totalCount: 0, facets: [] }
        }
      }
      console.error('Typesense search error:', error)
      return { hits: [], totalCount: 0, facets: [] }
    }
  }

  async searchRecommendations(params: {
    excludeObjectId: string
    categories?: string[]
    creators?: string
    searchTerm?: string
    limit?: number
  }): Promise<QuickSearchResponse> {
    if (!this.client) {
      return Promise.resolve({ hits: [], totalCount: 0, facets: [] })
    }

    const limit = params.limit ?? 4
    const excludeIdEscaped = params.excludeObjectId
      .replace(/\\/g, '\\\\')
      .replace(/`/g, '\\`')
    const searchTerm = params.searchTerm?.trim()
    const creatorTerm = params.creators?.trim()
    const query = searchTerm || creatorTerm || '*'
    const queryBy = searchTerm ? 'title,creators' : 'creators,title'

    const filterParts: string[] = [
      'in_stock:true',
      'live:true',
      RECOMMENDATIONS_MIN_PRICE_FILTER,
      `object_id:!=\`${excludeIdEscaped}\``,
    ]

    if (params.categories && params.categories.length > 0) {
      filterParts.push(
        `categories:=[${params.categories.map(c => `\`${c}\``).join(',')}]`
      )
    }

    const searchParams: any = {
      q: query,
      query_by: queryBy,
      query_by_weights: RECOMMENDATIONS_QUERY_BY_WEIGHTS,
      text_match_type: 'max_score',
      demote_synonym_match: true,
      filter_by: filterParts.join(' && '),
      sort_by: TypesenseClient.SortBy.RELEVANCE,
      include_fields: RECOMMENDATION_INCLUDE_FIELDS,
      limit,
      page: 1,
    }
    const searchContext = {
      source: 'recommendations',
      collection: 'products',
      queryLength: query.trim().length,
      queryType: query === '*' ? 'wildcard' : 'text',
      limit,
      hasCategoryFilter: Boolean(params.categories?.length),
      timeoutSeconds: TYPESENSE_CONNECTION_TIMEOUT_SECONDS,
      numRetries: TYPESENSE_NUM_RETRIES,
    }

    if (isTypesenseCircuitOpen()) {
      warnTypesenseCircuitOpen(
        'Typesense circuit open, returning empty recommendation results',
        searchContext
      )
      return { hits: [], totalCount: 0, facets: [] }
    }

    try {
      const response = await this.client
        .collections('products')
        .documents()
        .search(searchParams)

      recordTypesenseSuccess()

      const hits =
        response.hits
          ?.map((hit: any) => ({
            title: hit.document.title,
            creators: hit.document.creators,
            thumbnailUrl: hit.document.thumbnail_url,
            productUrl: hit.document.product_url,
            objectId: hit.document.object_id,
            priceUsd: hit.document.price_usd,
            discountUsd: hit.document.discount_usd,
            priceGbp: hit.document.price_gbp,
            discountGbp: hit.document.discount_gbp,
            resultType: 'product' as const,
          }))
          .filter((h: any) => String(h.objectId) !== params.excludeObjectId)
          .slice(0, limit) ?? []

      // If we didn't get enough results with category filter, fall back to bestsellers
      if (
        hits.length < limit &&
        params.categories &&
        params.categories.length > 0
      ) {
        const fallbackParams: any = {
          q: query,
          query_by: queryBy,
          query_by_weights: RECOMMENDATIONS_QUERY_BY_WEIGHTS,
          demote_synonym_match: true,
          preset: 'products_preset',
          filter_by: [
            PRODUCTS_PRESET_BASE_FILTER,
            'in_stock:true',
            RECOMMENDATIONS_MIN_PRICE_FILTER,
            `object_id:!=\`${excludeIdEscaped}\``,
          ].join(' && '),
          sort_by: 'sales_rank:asc',
          include_fields: RECOMMENDATION_INCLUDE_FIELDS,
          limit,
          page: 1,
        }

        const fallbackResponse = await this.client
          .collections('products')
          .documents()
          .search(fallbackParams)

        recordTypesenseSuccess()

        const existingIds = new Set(hits.map((h: any) => String(h.objectId)))
        const fallbackHits =
          fallbackResponse.hits
            ?.filter(
              (hit: any) =>
                !existingIds.has(String(hit.document.object_id)) &&
                String(hit.document.object_id) !== params.excludeObjectId
            )
            .map((hit: any) => ({
              title: hit.document.title,
              creators: hit.document.creators,
              thumbnailUrl: hit.document.thumbnail_url,
              productUrl: hit.document.product_url,
              objectId: hit.document.object_id,
              priceUsd: hit.document.price_usd,
              discountUsd: hit.document.discount_usd,
              priceGbp: hit.document.price_gbp,
              discountGbp: hit.document.discount_gbp,
              resultType: 'product' as const,
            })) ?? []

        return {
          hits: [...hits, ...fallbackHits].slice(0, limit),
          totalCount: hits.length + fallbackHits.length,
          facets: [],
        }
      }

      return { hits, totalCount: response.found || 0, facets: [] }
    } catch (error) {
      if (isTypesenseTimeoutError(error)) {
        warnTypesenseTimeout(
          'Typesense recommendation timeout, returning empty results',
          error,
          searchContext
        )
        recordTypesenseTimeout()
      }

      return { hits: [], totalCount: 0, facets: [] }
    }
  }

  /**
   * Newest live products (for empty checkout). Caller typically shuffles a subset client-side.
   */
  async searchRecentProductsForEmptyCart(options?: {
    poolSize?: number
  }): Promise<QuickSearchResponse> {
    const poolSize = options?.poolSize ?? 20
    const includeFields = RECOMMENDATION_INCLUDE_FIELDS.split(',')
    return this.searchProducts({
      collection: 'products',
      query: '*',
      includeFields,
      limit: poolSize,
      page: 1,
      sortBy: TypesenseClient.SortBy.DATE_ADDED_NEW_TO_OLD,
      filterBy: ['IN_STOCK'],
    })
  }

  sortByTranslate(sortBy: QuickSearchSortOption): string {
    return TypesenseClient.SortBy[sortBy as keyof typeof TypesenseClient.SortBy]
  }
}

export default TypesenseClient
